import { ErrorHandler, Service } from '@angular/core';

/**
 * sessionStorage flag used to guard against an infinite reload loop. It is set
 * just before a reload and cleared on the next successful bootstrap (see
 * `main.ts`).
 */
export const CHUNK_RELOAD_FLAG = 'fbi-chunk-reloaded';

/**
 * Matches the runtime errors thrown when a lazy-loaded bundle can no longer be
 * fetched. This happens when a user has an old `index.html` open and then
 * navigates to a lazy route whose hashed chunk was removed by the latest
 * deploy (`aws s3 sync --delete`) — in any environment, not just UAT.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /Loading chunk [\w-]+ failed|ChunkLoadError|(error )?loading dynamically imported module|Failed to fetch dynamically imported module/i;

/**
 * Global error handler that recovers from stale-bundle navigation failures by
 * forcing a single reload, which re-fetches the (no-cache) `index.html` and the
 * current hashed bundles. All other errors are delegated to the default
 * console logging used at bootstrap.
 */
@Service({ autoProvided: false })
export class ChunkReloadErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (this.isChunkLoadError(error) && this.canReload()) {
      this.markReloaded();
      globalThis.location.reload();
      return;
    }

    console.error(error);
  }

  private isChunkLoadError(error: unknown): boolean {
    return CHUNK_LOAD_ERROR_PATTERN.test(this.extractMessage(error));
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    const message = (error as { message?: unknown } | null)?.message;
    return typeof message === 'string' ? message : '';
  }

  private canReload(): boolean {
    try {
      return globalThis.sessionStorage?.getItem(CHUNK_RELOAD_FLAG) !== '1';
    } catch {
      // sessionStorage can throw (e.g. disabled cookies). Without it we cannot
      // guard against a reload loop, so play it safe and skip the reload.
      return false;
    }
  }

  private markReloaded(): void {
    try {
      globalThis.sessionStorage?.setItem(CHUNK_RELOAD_FLAG, '1');
    } catch {
      // Ignore — see canReload().
    }
  }
}
